import React, { useState } from "react";

import XVerified from "../assets/x-verified.png";
import MetaVerified from "../assets/meta-verified.png";
import GreenVerified from "../assets/green-verified.png";
import AccountVerified from "../assets/account-verified.png";
import GradientVerified from "../assets/gradient-verified.png";
import BlackVerified from "../assets/black-verified.png";
import RedVerified from "../assets/red-verified.png";

/* ---------- reusable badge ---------- */

const VerifiedBox = ({ icon, label, bgColor, textColor }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        backgroundColor: bgColor,
        color: textColor,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      <img src={icon} alt={label} width={14} height={14} />
      {label}
    </span>
  );
};

/* ---------- main component ---------- */

export default function Badges() {
  /* ---------- layout ---------- */

  const pageStyle = {
    background: "white",
    minHeight: "100vh",
    padding: "40px",
    display: "grid",
    gap: "24px",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  };

  const wrapperStyle = {
    display: "flex",
    flexDirection: "column",
  };

  const cardStyle = {
    background: "#fff",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    aspectRatio: "1 / 1",
    border: "1px dashed #ddd",
  };

  const viewBtnStyle = {
    marginTop: "8px",
    alignSelf: "flex-start",
    background: "#e8e9eb",
    color: "black",
    padding: "4px 10px",
    borderRadius: "8px",
    border: "none",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  };

  /* ---------- data ---------- */

  const badges = [
    { name: "X Verified", icon: XVerified, bg: "#E8F2FF", color: "#1D9BF0" },
    { name: "Meta Verified", icon: MetaVerified, bg: "#E9F1FF", color: "#1877F2" },
    { name: "Green Verified", icon: GreenVerified, bg: "#E9F9EF", color: "#1E8E3E" },
    { name: "Account Verified", icon: AccountVerified, bg: "#F2F3F5", color: "#444" },
    { name: "Gradient Verified", icon: GradientVerified, bg: "#F3ECFF", color: "#7B4DFF" },
    { name: "Black Verified", icon: BlackVerified, bg: "#F1F1F1", color: "#111" },
    { name: "Red Verified", icon: RedVerified, bg: "#FFE9E9", color: "#D93025" },
  ];

  /* ---------- modal state ---------- */

  const [openCode, setOpenCode] = useState(null);
  const [tab, setTab] = useState("react");

  /* ---------- helpers ---------- */

  const getFileName = (path) => path.split("/").pop();

  const downloadImage = (badge) => {
    const link = document.createElement("a");
    link.href = badge.icon;
    link.download = getFileName(badge.icon);
    link.click();
  };

  /* ---------- code generators ---------- */

  const generateReactCode = (b) => `
import BadgeIcon from "../assets/${getFileName(b.icon)}";

<span
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: "${b.bg}",
    color: "${b.color}",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
  }}
>
  <img src={BadgeIcon} alt="${b.name}" width={14} height={14} />
  ${b.name}
</span>
`.trim();

  const generateHTMLCode = (b) => `
<span
  style="
    display:inline-flex;
    align-items:center;
    gap:6px;
    background:${b.bg};
    color:${b.color};
    padding:4px 10px;
    border-radius:999px;
    font-size:12px;
    font-weight:600;
    line-height:1;
  "
>
  <img src="${getFileName(b.icon)}" width="14" height="14" />
  ${b.name}
</span>
`.trim();

  const downloadJSX = (b) => {
    const blob = new Blob([generateReactCode(b)], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${b.name.toLowerCase().replace(/\s/g, "-")}.jsx`;
    link.click();
  };

  /* ---------- render ---------- */

  return (
    <>
      <div style={pageStyle}>
        {badges.map((b, i) => (
          <div key={i} style={wrapperStyle}>
            <div style={cardStyle}>
              <VerifiedBox
                icon={b.icon}
                label={b.name}
                bgColor={b.bg}
                textColor={b.color}
              />
            </div>

            <button
              style={viewBtnStyle}
              onClick={() => {
                setOpenCode(b);
                setTab("react");
              }}
            >
              View Code
            </button>
          </div>
        ))}
      </div>

      {/* ---------- MODAL ---------- */}

      {openCode && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "640px",
              maxHeight: "90vh",
              background: "#0b0f14",
              color: "#e5e7eb",
              borderRadius: "12px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong>{openCode.name} Code</strong>
              <button
                onClick={() => setOpenCode(null)}
                style={{ background: "none", border: "none", color: "#fff" }}
              >
                ✕
              </button>
            </div>

            {/* tabs */}
            <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
              {["react", "html"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "none",
                    background: tab === t ? "green" : "#1f2933",
                    color: "#fff",
                  }}
                >
                  {t.toUpperCase()}
                </button>
              ))}
            </div>

            {/* code */}
            <pre
              style={{
                marginTop: "12px",
                background: "#020617",
                padding: "14px",
                borderRadius: "8px",
                maxHeight: "35vh",
                overflow: "auto",
                color: "#ddd",
                fontSize: "13px",
              }}
            >
              {tab === "react"
                ? generateReactCode(openCode)
                : generateHTMLCode(openCode)}
            </pre>

            {/* ---------- HOW TO USE ---------- */}
            <div
              style={{
                marginTop: "12px",
                background: "#020617",
                padding: "12px",
                borderRadius: "8px",
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              <strong style={{ color: "#22c55e" }}>
                How to use this badge
              </strong>

              <ol style={{ marginTop: 8, paddingLeft: 16 }}>
                <li>Click <b>Download Image</b> below.</li>
                <li>
                  Put it inside:
                  <br />
                  <code style={{ color: "#60a5fa" }}>
                    src/assets/{getFileName(openCode.icon)}
                  </code>
                </li>
                <li>
                  Import it in your component:
                  <br />
                  <code style={{ color: "#facc15" }}>
                    import BadgeIcon from "../assets/{getFileName(openCode.icon)}";
                  </code>
                </li>
                <li>Paste the code above — done 🎉</li>
              </ol>
            </div>

            {/* actions */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "12px",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() =>
                  navigator.clipboard.writeText(
                    tab === "react"
                      ? generateReactCode(openCode)
                      : generateHTMLCode(openCode)
                  )
                }
                style={{
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "none",
                  background: "green",
                  color: "#fff",
                }}
              >
                Copy Code
              </button>

              {tab === "react" && (
                <button
                  onClick={() => downloadJSX(openCode)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "none",
                    background: "#1d4ed8",
                    color: "#fff",
                  }}
                >
                  Download .jsx
                </button>
              )}

              <button
                onClick={() => downloadImage(openCode)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#9333ea",
                  color: "#fff",
                }}
              >
                Download Image
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
